import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ArrowLeft, Calendar, Pill, AlertCircle, Heart, Settings, Phone, MapPin, Building2, Globe, Linkedin, Twitter } from "lucide-react";

const COMMON_CONDITIONS = [
  "Diabetes",
  "High Blood Pressure",
  "Heart Disease",
  "Asthma",
  "Arthritis",
  "Depression/Anxiety",
  "Thyroid Disorder",
  "Cancer History"
];

interface RiskFlags {
  allergies?: string[];
  medications?: string[];
  conditions?: string[];
}

interface AdminProfile {
  telephone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  companyName?: string;
  jobTitle?: string;
  website?: string;
  linkedin?: string;
  twitter?: string;
  bio?: string;
}

const ProfileSettingsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Patient form fields
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    allergies: "",
    medications: "",
    conditions: [] as string[]
  });

  // Admin form fields
  const [adminForm, setAdminForm] = useState<AdminProfile>({
    telephone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    companyName: "",
    jobTitle: "",
    website: "",
    linkedin: "",
    twitter: "",
    bio: ""
  });

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Check if user is admin and load appropriate profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      
      try {
        // Check if user has admin role
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id);

        const userIsAdmin = roles?.some(r => r.role === 'agency_admin' || r.role === 'super_admin' || r.role === 'supervisor');
        setIsAdmin(!!userIsAdmin);

        if (userIsAdmin) {
          // Load admin profile from profiles table
          const { data: profile, error } = await supabase
            .from("profiles")
            .select("*")
            .eq("user_id", user.id)
            .maybeSingle();

          if (error) throw error;

          if (profile) {
            // Parse admin_metadata if it exists
            const metadata = (profile as any).admin_metadata || {};
            setAdminForm({
              telephone: metadata.telephone || "",
              address: metadata.address || "",
              city: metadata.city || "",
              state: metadata.state || "",
              zipCode: metadata.zipCode || "",
              companyName: metadata.companyName || "",
              jobTitle: metadata.jobTitle || "",
              website: metadata.website || "",
              linkedin: metadata.linkedin || "",
              twitter: metadata.twitter || "",
              bio: metadata.bio || ""
            });
          }
        } else {
          // Load patient profile
          const { data: patient, error } = await supabase
            .from("patients")
            .select("first_name, last_name, dob, risk_flags")
            .eq("user_id", user.id)
            .maybeSingle();

          if (error) throw error;

          if (patient) {
            const riskFlags = (patient.risk_flags || {}) as RiskFlags;
            setForm({
              firstName: patient.first_name || "",
              lastName: patient.last_name || "",
              dob: patient.dob || "",
              allergies: (riskFlags.allergies || []).join(", "),
              medications: (riskFlags.medications || []).join(", "),
              conditions: riskFlags.conditions || []
            });
          }
        }
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    if (user) {
      loadProfile();
    }
  }, [user]);

  const toggleCondition = (condition: string) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.includes(condition)
        ? prev.conditions.filter(c => c !== condition)
        : [...prev.conditions, condition]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    setLoading(true);

    try {
      if (isAdmin) {
        // Update admin profile - store extra fields in a metadata column or separate update
        const { error } = await supabase
          .from("profiles")
          .update({
            admin_metadata: adminForm
          } as any)
          .eq("user_id", user.id);

        if (error) throw error;
      } else {
        // Basic validation for patient
        if (form.firstName.trim().length < 1 || form.firstName.length > 100) {
          toast({
            title: "Invalid first name",
            description: "First name must be between 1 and 100 characters",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        
        if (form.lastName.trim().length < 1 || form.lastName.length > 100) {
          toast({
            title: "Invalid last name",
            description: "Last name must be between 1 and 100 characters",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        // Build risk_flags object with medical history
        const riskFlags = {
          allergies: form.allergies.split(',').map(a => a.trim()).filter(Boolean),
          medications: form.medications.split(',').map(m => m.trim()).filter(Boolean),
          conditions: form.conditions
        };

        // Update the patient record
        const { error } = await supabase
          .from("patients")
          .update({
            first_name: form.firstName.trim(),
            last_name: form.lastName.trim(),
            dob: form.dob || null,
            risk_flags: riskFlags
          })
          .eq("user_id", user.id);

        if (error) throw error;
      }

      // Refresh profile to get updated data
      await refreshProfile();

      toast({
        title: "Profile updated",
        description: "Your changes have been saved",
      });
    } catch (error: any) {
      console.error("Profile update error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Profile Settings - CheckFirstCare</title>
        <meta name="description" content="Manage your CheckFirstCare profile and medical information." />
      </Helmet>
      
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="container max-w-2xl mx-auto">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Settings className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Profile Settings</h1>
                <p className="text-muted-foreground">
                  {isAdmin ? "Manage your professional profile" : "Manage your personal and medical information"}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {isAdmin ? (
              <>
                {/* Admin Contact Information Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="w-5 h-5 text-primary" />
                      Contact Information
                    </CardTitle>
                    <CardDescription>
                      Your contact details for communication
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="telephone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Telephone
                      </Label>
                      <Input
                        id="telephone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={adminForm.telephone}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, telephone: e.target.value }))}
                      />
                    </div>

                    <div className="text-sm text-muted-foreground">
                      Email: {user?.email}
                    </div>
                  </CardContent>
                </Card>

                {/* Admin Address Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      Address
                    </CardTitle>
                    <CardDescription>
                      Your office or business address
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="address">Street Address</Label>
                      <Input
                        id="address"
                        type="text"
                        placeholder="123 Main Street, Suite 100"
                        value={adminForm.address}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, address: e.target.value }))}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          type="text"
                          placeholder="New York"
                          value={adminForm.city}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, city: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          type="text"
                          placeholder="NY"
                          value={adminForm.state}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, state: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="zipCode">ZIP Code</Label>
                        <Input
                          id="zipCode"
                          type="text"
                          placeholder="10001"
                          value={adminForm.zipCode}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, zipCode: e.target.value }))}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Admin Professional Information Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-primary" />
                      Professional Information
                    </CardTitle>
                    <CardDescription>
                      Your company and role details
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name</Label>
                        <Input
                          id="companyName"
                          type="text"
                          placeholder="Healthcare Corp"
                          value={adminForm.companyName}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, companyName: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="jobTitle">Job Title</Label>
                        <Input
                          id="jobTitle"
                          type="text"
                          placeholder="Administrator"
                          value={adminForm.jobTitle}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, jobTitle: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bio">Bio / About</Label>
                      <Textarea
                        id="bio"
                        placeholder="A brief description about yourself and your role..."
                        value={adminForm.bio}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, bio: e.target.value }))}
                        rows={3}
                        maxLength={500}
                      />
                      <p className="text-xs text-muted-foreground">
                        Max 500 characters
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Admin Social & Web Presence Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-primary" />
                      Online Presence
                    </CardTitle>
                    <CardDescription>
                      Your website and social media profiles
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="website" className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Website
                      </Label>
                      <Input
                        id="website"
                        type="url"
                        placeholder="https://www.example.com"
                        value={adminForm.website}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, website: e.target.value }))}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="linkedin" className="flex items-center gap-2">
                          <Linkedin className="w-4 h-4" />
                          LinkedIn
                        </Label>
                        <Input
                          id="linkedin"
                          type="url"
                          placeholder="https://linkedin.com/in/username"
                          value={adminForm.linkedin}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, linkedin: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="twitter" className="flex items-center gap-2">
                          <Twitter className="w-4 h-4" />
                          Twitter / X
                        </Label>
                        <Input
                          id="twitter"
                          type="url"
                          placeholder="https://twitter.com/username"
                          value={adminForm.twitter}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, twitter: e.target.value }))}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* Patient Personal Information Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                    <CardDescription>
                      Your basic profile details
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          type="text"
                          placeholder="John"
                          value={form.firstName}
                          onChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                          maxLength={100}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          type="text"
                          placeholder="Doe"
                          value={form.lastName}
                          onChange={(e) => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                          maxLength={100}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="dob" className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Date of Birth
                      </Label>
                      <Input
                        id="dob"
                        type="date"
                        value={form.dob}
                        onChange={(e) => setForm(prev => ({ ...prev, dob: e.target.value }))}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="text-sm text-muted-foreground">
                      Email: {user?.email}
                    </div>
                  </CardContent>
                </Card>

                {/* Patient Medical History Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-primary" />
                      Medical History
                    </CardTitle>
                    <CardDescription>
                      This information helps provide accurate triage recommendations
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Conditions */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Existing Conditions
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {COMMON_CONDITIONS.map((condition) => (
                          <Badge
                            key={condition}
                            variant={form.conditions.includes(condition) ? "default" : "outline"}
                            className="cursor-pointer hover:bg-primary/20 transition-colors"
                            onClick={() => toggleCondition(condition)}
                          >
                            {condition}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Allergies */}
                    <div className="space-y-2">
                      <Label htmlFor="allergies" className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Known Allergies
                      </Label>
                      <Textarea
                        id="allergies"
                        placeholder="e.g., Penicillin, Peanuts, Latex (separate with commas)"
                        value={form.allergies}
                        onChange={(e) => setForm(prev => ({ ...prev, allergies: e.target.value }))}
                        maxLength={500}
                        rows={2}
                      />
                      <p className="text-xs text-muted-foreground">
                        Include drug, food, and environmental allergies
                      </p>
                    </div>

                    {/* Medications */}
                    <div className="space-y-2">
                      <Label htmlFor="medications" className="flex items-center gap-2">
                        <Pill className="w-4 h-4" />
                        Current Medications
                      </Label>
                      <Textarea
                        id="medications"
                        placeholder="e.g., Lisinopril 10mg, Metformin 500mg (separate with commas)"
                        value={form.medications}
                        onChange={(e) => setForm(prev => ({ ...prev, medications: e.target.value }))}
                        maxLength={500}
                        rows={2}
                      />
                      <p className="text-xs text-muted-foreground">
                        Include prescription and over-the-counter medications
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Save Button */}
            <div className="flex justify-end gap-3">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || (!isAdmin && (!form.firstName.trim() || !form.lastName.trim()))}
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ProfileSettingsPage;
